/**
 * Turn an API error into something a parent or staff member can act on.
 *
 * The API's `error` field is frequently a MACHINE CODE (`VALIDATION_ERROR`,
 * `REGISTRATION_PENDING`, `INVALID_ATTACHMENT_KEY`), while `message` carries
 * the human sentence — so a raw code must never be shown when richer detail
 * is available.
 *
 * Order of preference:
 *   1. `data.message`        — a real sentence, when the API sent one
 *   2. a friendly mapping    — for known codes that ship no message
 *   3. field-level detail    — zod issues, so the user knows WHICH field
 *   4. the raw code          — last resort, better than "something went wrong"
 */

// Codes the API returns without an accompanying human message. Keep this to
// codes users actually hit; anything unmapped still degrades to the code
// itself rather than being swallowed.
const FRIENDLY_MESSAGES: Record<string, string> = {
  REGISTRATION_PENDING:
    "This child's registration isn't complete yet. Finish the registration form first.",
  VALIDATION_ERROR: 'Some required details are missing or invalid.',
  SIGNED_NAME_REQUIRED: 'Please type your full name to sign.',
  SIGNED_NAME_MISMATCH: 'The typed name must match the name on your account.',
  INVALID_ATTACHMENT_KEY: "That attachment couldn't be verified. Please re-upload the file.",
  ATTACHMENT_MISSING: "That attachment wasn't uploaded. Please try attaching it again.",
  CLASS_NOT_SCHEDULED_ON_DATE: "This class isn't scheduled to run on that date.",
  INVALID_TRANSITION: "That change isn't allowed from the record's current state.",
  NOT_NOTIFIED: 'Every guardian must be notified before this can be closed.',
  TUSLA_REPORT_REQUIRED: 'Record the Tusla report before closing this incident.',
  MUST_STAY_ACTIVE: 'At least one active form must remain for this location.',
  ACCESS_DENIED: "You don't have permission to do that.",
  FORBIDDEN: "You don't have permission to do that.",
};

/** Field-level detail from a zod-backed 400, e.g. "childId: Required". */
function describeDetails(details: unknown): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;
  const parts = details
    .map((d: any) => {
      const path = Array.isArray(d?.path) ? d.path.filter(Boolean).join('.') : '';
      const msg = typeof d?.message === 'string' ? d.message : '';
      if (!msg) return null;
      return path ? `${path}: ${msg}` : msg;
    })
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

export function getErrorMessage(error: any): string {
  const data = error?.data;

  // 1. A real sentence from the API always wins.
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;

  const code = typeof data?.error === 'string' ? data.error : '';

  // 2. Known code with no message -> friendly copy.
  if (code && FRIENDLY_MESSAGES[code]) return FRIENDLY_MESSAGES[code];

  // 3. Field-level validation detail, so the user knows what to fix.
  const detail = describeDetails(data?.details);
  if (detail) return code ? `${FRIENDLY_MESSAGES[code] ?? code}: ${detail}` : detail;

  // 4. Fall back to whatever we have.
  return code || error?.message || 'An unexpected error occurred';
}
